import { z } from 'zod';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { Location } from '@brightlocal/api-client';
import { LocationIdSchema } from '@brightlocal/shared';

import { prefixKey } from 'shared/utils/localStorage';

import { RECENT_LOCATIONS_LIMIT, RECENT_LOCATIONS_STORAGE_KEY } from '../consts';
import { formatLocationStreetAddress } from '../utils/formatLocationStreetAddress';

const RecentLocationSchema = z.object({
  locationId: LocationIdSchema,
  businessName: z.string(),
  address: z.string()
});

const persistedRecentLocationsSchema = z.object({
  recentsByUser: z.record(z.string(), z.array(RecentLocationSchema))
});

/** One entry of the sidebar switcher's Recents list — enough to render `Name • Address` without a request. */
export type RecentLocation = z.infer<typeof RecentLocationSchema>;

/** auth/me `user.userId` — the recents are kept per user so two people sharing a browser never see each other's list. */
type UserId = number;

/** Params for {@link RecentLocationsStore.addRecentLocation}. */
interface AddRecentLocationParams {
  /** The user who opened the location. */
  userId: UserId;
  /** The location that was just opened. */
  location: RecentLocation;
}

interface RecentLocationsStore {
  /** Recently opened locations per user id, most recent first. */
  recentsByUser: Partial<Record<UserId, RecentLocation[]>>;
  /** Moves the location to the front of the user's list, dropping a duplicate and anything past the limit. */
  addRecentLocation: (params: AddRecentLocationParams) => void;
}

const NO_RECENT_LOCATIONS: RecentLocation[] = [];

/**
 * Recently opened locations for the sidebar location switcher. Persisted in localStorage under
 * `brightlocal_recent_locations`, keyed by user id. Written by {@link rememberRecentLocation} from
 * the `$id` route loader, so every visit to a location page is recorded before the shell renders.
 */
export const useRecentLocationsStore = create<RecentLocationsStore>()(
  persist(
    (set) => ({
      recentsByUser: {},
      addRecentLocation: ({ userId, location }) =>
        set((state) => {
          const otherRecents = (state.recentsByUser[userId] ?? NO_RECENT_LOCATIONS).filter(
            (recent) => recent.locationId !== location.locationId
          );

          return {
            recentsByUser: {
              ...state.recentsByUser,
              [userId]: [location, ...otherRecents].slice(0, RECENT_LOCATIONS_LIMIT)
            }
          };
        })
    }),
    {
      name: prefixKey(RECENT_LOCATIONS_STORAGE_KEY),
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ recentsByUser: state.recentsByUser }),
      // A corrupt stored value starts every list empty instead of poisoning the store.
      merge: (persisted, current) => {
        const parsed = persistedRecentLocationsSchema.safeParse(persisted);

        return { ...current, recentsByUser: parsed.success ? parsed.data.recentsByUser : {} };
      }
    }
  )
);

/** Selects the given user's recents, most recent first — an empty list until a location has been opened. */
export const selectRecentLocations = (userId: UserId | undefined) => (state: RecentLocationsStore) =>
  userId === undefined ? NO_RECENT_LOCATIONS : (state.recentsByUser[userId] ?? NO_RECENT_LOCATIONS);

/** Params for {@link rememberRecentLocation}. */
interface RememberRecentLocationParams {
  /** The user who opened the location. */
  userId: UserId;
  /** The location as loaded by the `$id` route. */
  location: Location;
}

/** Records a location visit for the switcher's Recents. Safe to call outside React — it writes to the store directly. */
export const rememberRecentLocation = ({ userId, location }: RememberRecentLocationParams) =>
  useRecentLocationsStore.getState().addRecentLocation({
    userId,
    location: {
      locationId: location.locationId,
      businessName: location.businessName,
      address: formatLocationStreetAddress(location.address)
    }
  });
